<template>
    <div>
        <div class="pb--15">
            <template v-if="pageLoading">
                <PageSpinner />
            </template>

            <template v-else-if="pageLoadingErr">
                <EmptyPage state="error" type="task" @action="loadPage" />
            </template>

            <template v-else>
                <div class="w-100 task__details__row">
                    <div class="task__details__row__left">
                        <div class="task__details__row__header">
                            <div class="flex align-items-center">
                                <span class="text-gray-400 mr--5 text--sm">Linked project: </span>
                                <FormLabel 
                                    label-type="menu" 
                                    html-for="taskDetails" 
                                    :title="selectedProject.title" 
                                    class="mb--0 cursor-pointer text--underline text--color-primary"
                                    @click="goToParentProject" 
                                /> 
                            </div>  

                            <div v-if="showActionBtns" class="flex align-items-center gap-2">
                                <MainFilter page-type="tasks" />
                                <Group class="ml--5" :items="tasksGroup" :value="groupSelection" @select="setGroupSelection" />
                            </div>
                        </div>

                        <div class="task__details__row__list">
                            <template v-if="!tasks.length">
                                <EmptyPage v-if="isSearched" state="no_result" />
    
                                <EmptyPage
                                    v-else
                                    state="empty" 
                                    type="task" 
                                    icon-name="task"
                                />
                            </template>
    
                            <template v-else>
                                <TaskList 
                                    :group-selection="groupSelection"
                                    :tasks="tasks"
                                    :current-item-id="currentTaskId" 
                                    @set-current-task="setCurrentTask"
                                />
                            </template>

                            <TaskForm 
                                :loading="addTaskLoading" 
                                :project-id="projectId"
                                @form-complete="handleAddTask" 
                            />
                        </div>
                    </div>

                    <div class="task__details__row__right">
                        <TaskDetails 
                            :current-item-id="currentTaskId" 
                            task-type="child" 
                            :group-selection="groupSelection"
                            @on-duplicate="handleDuplicateTask"
                            @on-delete="handleDeleteTask"
                            @open-reminders-modal="openRemindersModal"
                            @set-current="v => currentTask = v" 
                        />
                    </div>
                </div>
            </template>
        </div>

        <MainModal 
            :show-modal="modalState === 'manageRemindersUpgrade'" 
            size="sm" 
            :close-btn="false" 
        >
            <template #body>
                <div class="modal--section">
                    <div class="upgrade--image">
                        <SvgAssets name="alarm" width="50px" height="50px" class="bg-white" />
                    </div>

                    <div class="upgrade--text">   
                        <div>
                            <div class="flex align-items-center mb--5">
                                <h2 class="upgrade--title mb--0">
                                    Set reminders
                                </h2>

                                <span
                                    v-if="accountPlan === 'free'" 
                                    class="status__map text--medium tag bold badge rounded-pill text--xxs tag--pink text--upper ml--10"
                                >
                                    <span 
                                        class="status__circle tag--pink__text text--xxs" 
                                    />
                                    Paid plans only
                                </span>
                            </div>
                            <p class="upgrade--subtitle error">
                                Reminders help control the frequency of email reminders your clients get for partially paid or no-show invoices.
                            </p>

                            <p v-if="accountPlan === 'standard'" class="upgrade--subtitle error">
                                You have exhausted the number of reminder credits for this month on your account plan.
                            </p>
                        </div>
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton 
                        class="mr--5" 
                        variant="outline"
                        label="Cancel" 
                        @submit="modalState = ''"
                    />

                    <BaseButton 
                        type="submit" 
                        label="Go to settings" 
                        @submit="goToSettings(true)"
                    />
                </div>
            </template>
        </MainModal>

        <MainModal 
            :show-modal="modalState === 'showRemindersModal'" 
            size="md" 
            title="Manage reminders"
            :close-btn="false" 
        >
            <template #body>
                <Callouts class="primary mb--25">
                    <!-- <p class="text--sm invoice__client__text text-gray-600 lh-base mb--0">
                        Reminders are a great way to control the frequency of email notififications sent to you. 
                    </p> -->
                    <p class="text--sm invoice__client__text text-gray-600 lh-base mb--0 mt--10">
                        <strong>Note</strong>: selecting an option (e.g, <i>"30 days before"</i>) <strong>=</strong> sends email notifications to clients <i>30 days</i> before the invoice payment is due.
                    </p>
                </Callouts>
                
                <form @submit.prevent="">
                    <div class="col-12 mb--10 invoice__details__divider pb--5">
                        <div class="flex w--100 align-items-center justify-content-between">
                            <FormLabel title="Select one or more reminders" label-type="menu" class="mb--0" />
                            
                            <BaseButton 
                                class="p--0"
                                :label="!setDailyReminder ? '* Set recurring' : '+Use options'" 
                                variant="ghost" 
                                variant-style="primary"
                                medium
                                @submit="toggleReminder"
                            />
                        </div>
                    </div>
        
                    <div class="form-check-items" :class="{ 'disabled': setDailyReminder }">
                        <div v-for="item in reminderEnum" :key="item" class="check-item form-check-item" :class="{ 'active': currentTask.reminders?.includes(item.id) }">
                            <div class="form-check form-check-inline align-items-center text--sm mb--0">
                                <input
                                    v-model="currentTask.reminders" class="form-check-input" type="checkbox"
                                    :value="item.id"
                                >
                                <label class="form-check-label mb--0" for="inlineCheckbox1">{{ item.name }}</label>
                            </div>
                        </div>
                    </div>
                </form>
            </template>

            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton 
                        class="mr--5" 
                        variant="outline"
                        label="Cancel" 
                        :disabled="updateRemindersLoading"
                        @submit="modalState = ''"
                    />

                    <BaseButton 
                        type="submit" 
                        label="Update" 
                        :loading="updateRemindersLoading"
                        @submit="manageReminders"
                    />
                </div>
            </template>
        </MainModal>
    </div>
</template>

<script>
import {
    EmptyPage,
    PageSpinner,
    MainFilter,
    Group,
    FormLabel,
    BaseButton,
} from "@/components/ui/index";
import { assembleQueryList } from "@/utils/helpers";
import TaskList from "@/components/tasks/TaskList.vue";
import TaskDetails from "@/components/tasks/TaskDetails.vue";
import TaskForm from "@/components/tasks/TaskForm.vue";
import { tasksGroup } from "@/utils/dummy";
import MainModal from "@/components/ui/modals/MainModal.vue";
import SvgAssets from "@/components/ui/icons/SvgAssets.vue";
import Callouts from "@/components/ui/alert/Callouts.vue";

export default {
    name: "ProjectTasksView",

    components: {
        EmptyPage,
        PageSpinner,
        MainFilter,
        TaskDetails, 
        Group,
        TaskList,
        TaskForm,
        FormLabel,
        BaseButton,
        MainModal,
        SvgAssets,
        Callouts,
    },

    data() {
        return {
            pageLoading: false,
            pageLoadingErr: false,
            pageLoadingErrMsg: "",
            currentTaskId: "",
            notes: [],
            notePageData: {
                currentPage: 1,
                totalPages: 1,
                pageSize: 50,
                total: 0,
            },
            tasks: [],
            pageData: {
                currentPage: 1,
                totalPages: 1,
                pageSize: 50,
                total: 0,
            },
            tasksGroup,
            reminderEnum: [
                { id: "30_days_before", name: "30 days before"},
                { id: "21_days_before", name: "21 days before"},
                { id: "14_days_before", name: "14 days before"},
                { id: "7_days_before", name: "7 days before"},
                { id: "3_days_before", name: "3 days before"},
                { id: "a_day_before", name: "A day before"},
                { id: "on_due_date", name: "On"},
            ],
            groupSelection: "none",
            unableToLoadPage: false,
            addTaskLoading: false,
            projectId: this.$route.params.id,
            selectedProject: {
                _id: "",
                title: "",
            },
            modalState: "",
            currentTask: {
                reminders: [],
                deadline: 0,
            },
            updateRemindersLoading: false,
            setDailyReminder: false,
        };
    },

    computed: {
        showActionBtns() {
            if (this.pageData?.total) {
                return true;
            }

            if (this.isSearched) {
                return true;
            }
            
            return false;
        },
        
        isSearched() {
            return Object.keys(this.$route.query).length > 0;
        },

        accountPlan() {
            return this.$store.getters["subscription/subscriptionInfo"]?.plan || "free";
        },
    },

    watch: {
        "$route.query": {
            async handler(newVal) {                                
                await this.handleFetchTasks({ refresh: true, ...newVal });
            },
            deep: true,
        },

        "groupSelection": {
            async handler(newVal, oldVal) {
                await this.handleFetchTasks({ groupBy: newVal, refresh: true });
            },
        },

        async refreshList(newVal) {
            if (newVal) {
                await this.handleFetchTasks({ refresh: true });
            }
        },

        setDailyReminder(newVal) {
            if (newVal) {
                this.currentTask.reminders = [];
            }
        },
    },

    async created() {
        await this.getGroupSelectionOnLoad();
        await this.loadPage();
    },

    methods: {
        getGroupSelectionOnLoad() {
            const groupSelection = localStorage.getItem("z_group_project_task_selection");

            if (groupSelection) {
                this.groupSelection = groupSelection;
                this.setCurrentTask(this.tasks[0]?.tasks[0]);
            } else {
                this.setCurrentTask(this.tasks[0]);
            }
        },

        async handleFetchProject() {
            try {
                this.unableToLoadPage = false;
                const id = this.$route.params.id;
                
                if (id === "undefined" || !id) {
                    this.unableToLoadPage = true;
                    return;
                }
    
                const res = await this.$store.dispatch("project/getProject", id);
                this.selectedProject = res;
            } catch (error) {
                throw error;
            }
        },

        async loadPage(backgroundRefresh = false) {            
            try {    
                if (!backgroundRefresh) {
                    this.pageLoading = true;
                    this.pageLoadingErr = false;
                }            

                if (!this.selectedProject?.title) {
                    await this.handleFetchProject();
                }   
                               
                if (this.groupSelection === "none") {
                    await this.handleFetchTasks({ refresh: true });
                }
            } catch (error) {
                this.pageLoadingErr = true;
            } finally {
                this.pageLoading = false;
            }
        },

        async handleFetchTasks(params) {            
            try {
                if (params?.refresh) {
                    this.pageLoading = true;
                    this.pageLoadingErr = false;
                }
                const query = this.$route.query;
                const pageQueryObj = {
                    page: query?.page || this.pageData.currentPage,
                    type: "project",
                    projectId: this.$route.params.id,
                    groupBy: query?.groupBy || this.groupSelection,
                    ...query,
                    ...params,
                };

                const buildResult = assembleQueryList(pageQueryObj);
                const res = await this.$store.dispatch("project/fetchTasks", { params: buildResult });
                
                this.tasks  = res.data;
                this.pageData = {
                    ...this.pageData,
                    ...res.pageDetails,
                };
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.pageLoadingErr = true;
                this.pageLoadingErrMsg = errMsg;
            } finally {
                this.pageLoading = false;
            }
        },  

        setGroupSelection(val) {
            this.groupSelection = val;
            localStorage.setItem("z_group_project_task_selection", val);
        },

        setCurrentTask(task) {                        
            if (!task?._id) return;

            console.log({ setCurrentTask: task });

            this.currentTaskId = task._id;            
            this.currentTask = task;
            this.currentTask.reminders = task?.reminders || [];
        },

        async manageReminders() {
            try {
                this.updateRemindersLoading = true;

                const res = await this.$store.dispatch("task/editTask", { 
                    payload: {
                        reminders: this.currentTask?.reminders,
                        _id: this.currentTask?._id,
                        name: this.currentTask?.name,
                    },
                    groupBy: this.groupSelection,
                });
                
                this.currentTask.reminders = res?.reminders;
                this.modalState = "";
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg || "An error occurred while fetching rate cards.");
            } finally {
                this.updateRemindersLoading = false;
            }
        },

        resetCurrentTask() {                        
            this.currentTaskId = "";
        },

        async updateTasksAfterDeletion(id) {
            if (this.groupSelection === "none") {
                const tasks = this.tasks;            
                const updatedTasks = tasks.filter(c => c._id !== id);

                this.tasks = updatedTasks;
            } else {
                await this.loadPage(true);
            }
            this.tasks = this.tasks.filter();
        },

        async handleAddTask(data) {
            try {
                this.addTaskLoading = true;

                const resp = await this.$store.dispatch("task/addTask", data.payload);
                this.$toast.success("Task added");
                
                this.tasks = [ ...this.tasks, resp ];
                this.pageData.total = this.pageData.total + 1,
                this.pageData.totalPages = Math.ceil((this.pageData.total + 1) / 50);

                data.resetFn && data.resetFn();
                this.setCurrentTask(resp);

                if (this.tasks.length === 1) {
                    await this.$store.dispatch("app/getOnboardingChecks");
                }
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;                
                this.$toast.error(errMsg);
            } finally {
                this.addTaskLoading = false;
            }
        },

        async handleDuplicateTask(data) {
            try {
                const resp = await this.$store.dispatch("task/duplicateTask", { task: data.task, payload: data.payload, type: data.type });
                this.refreshList = true;

                this.setCurrentTask(resp);
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg || "An error occurred while fetching rate cards.");
            }
        },

        async handleDeleteTask(payload) {
            await this.$store.dispatch("project/deleteTask", payload.taskId);

            if (this.groupSelection === "none") {
                const updatedTasks = this.tasks.filter(c => c._id !== payload.taskId);
                this.tasks = updatedTasks;
            } else {
                // await this.handleFetchTasks({ refresh: true });
                await this.handleFetchTasks();
            }
        },

        goToParentProject() {
            const id = this.$route.params.id;

            if (!id) return;

            this.$router.push(`/dashboard/projects/${id}`);
        },

        goToSettings(isModal = false) {
            if (isModal) {
                this.modalState = "";
            }
            this.$router.push("/dashboard/settings/payments-and-billings");
        },

        openRemindersModal() {          
            if (this.accountPlan === "free") {
                this.modalState = "manageRemindersUpgrade";
                return;
            }

            if (this.accountPlan === "standard") {
                const maxLimit = MAX_COUNT_BY_FEATURES[this.accountPlan].reminderCount;

                if (this.projectRemindersCount >= maxLimit) {
                    this.upgradeActionType = "project_reminders_exhausted";
                    this.modalActionType = "showUpgradeModalAlttt";
                    return;
                }
            }

            if (!this.currentTask.deadline) {
                this.$toast.info("Set a deadline date to use this feature");
                return;
            }

            this.modalState = "showRemindersModal";
        },

        toggleReminder() {            
            this.setDailyReminder = !this.setDailyReminder;
        },
    },
};
</script>

<style lang="scss" scoped>
.form-check-items {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 5px;
    margin-top: 10px;
}

.check-item {
    display: flex;
    border: 1px solid #cbd5e1;
    padding: 3px 10px;
    background: #fff;
    font-size: 0.875rem;
    border-radius: 6px;     

    &.active {
        border: 1px solid #289CA9;
    }

    .form-check-label {
        min-width: 80%;
    }
}
</style>